/**
 * #1467 — fill sounds for the polled Polymarket fill reads (international
 * `/api/polymarket/fills`, US `/api/polymarket-us/fills`).
 *
 * Kalshi fills reach `processFill`, which deduplicates them, records first-fill
 * status per order and hands each one to the shared gate (`announceFillSound`):
 * session start, 30 s recency, thresholds, tone, unlock queue and the cross-tab
 * claim. Polymarket fills only arrive as whole polled replies, so this module
 * plays the part `processFill` plays for Kalshi and then calls the same gate.
 * It adds no sound mechanism of its own.
 *
 * One feed per venue per tab, shared by every mounted reader (the rail can be
 * mounted more than once), so a row reaches the gate at most once per tab:
 *
 *   - The first validated reply for a scope (user, org, login epoch) is the
 *     baseline. Its rows are recorded and stay silent, like Kalshi's REST
 *     bootstrap. Its arrival time is the session start passed to the gate, so
 *     a fill that executed before it and shows up in a later reply (late
 *     ingest, backfill) stays silent too.
 *   - Later replies pass only rows not yet seen in that scope. A failed read or
 *     reconnect keeps the scope, so a row seen before the gap never replays;
 *     a row first seen after it still sounds if it is within the 30 s window.
 *   - A new scope (logout, org switch, new login epoch) starts a new baseline.
 *
 * Across tabs the gate's ledger key `[user, org, role, venue, ...fill id]`
 * makes one tab play each fill. A tab whose baseline already held a fill never
 * claims it, so a tab opened after a fill does not take it from an older one.
 */
import { announceFillSound } from './accountStore';
import { getAuthSessionEpoch } from './authStore';
import { recordSoundDecision } from '../lib/audio/soundDiagnostics';

export type PolledFillVenue = 'polymarket' | 'polymarket_us';

export interface PolledFill {
  /** The venue's stable fill identity: the same value in every tab. */
  fillKey: readonly string[];
  /** Order (or US intent) the fill belongs to; null when the row does not say. */
  orderId: string | null;
  count: number;
  /** Venue execution time; NaN when the row has no usable time (never sounds). */
  executedAtMs: number;
}

interface Feed {
  scope: string;
  startedAtMs: number;
  seen: Set<string>;
  seenOrder: string[];
  seenOrders: Set<string>;
}

/** Same bound as Kalshi's `_seenFillIds`; a reply carries at most 20 rows. */
const MAX_SEEN = 1000;
const feeds = new Map<PolledFillVenue, Feed>();

/**
 * Feed one validated reply. `identity` is the reader's own scope string; the
 * caller must already have checked that the reply belongs to the live login.
 * Rows go to the gate in reply order, as Kalshi's `ingestFillBatch` does.
 */
export function feedPolledFills(venue: PolledFillVenue, identity: string, rows: readonly PolledFill[]): void {
  const scope = JSON.stringify([identity, getAuthSessionEpoch()]);
  let feed = feeds.get(venue);
  const baseline = !feed || feed.scope !== scope;
  if (!feed || baseline) {
    feed = { scope, startedAtMs: Date.now(), seen: new Set(), seenOrder: [], seenOrders: new Set() };
    feeds.set(venue, feed);
  }
  for (const row of rows) {
    const key = JSON.stringify(row.fillKey);
    if (feed.seen.has(key)) continue;
    recordSoundDecision('received');
    feed.seen.add(key);
    feed.seenOrder.push(key);
    while (feed.seen.size > MAX_SEEN) {
      const oldest = feed.seenOrder.shift();
      if (oldest) feed.seen.delete(oldest);
    }
    // First-fill status is recorded for baseline rows too (#795): an order
    // whose earlier fills predate this page is not a new order.
    const isFirstFill = row.orderId !== null && !feed.seenOrders.has(row.orderId);
    if (row.orderId !== null) feed.seenOrders.add(row.orderId);
    if (baseline) { recordSoundDecision('preSession'); continue; }
    announceFillSound({ venue, fillKey: row.fillKey, count: row.count, createdAtMs: row.executedAtMs, isFirstFill },
      null, { atMs: feed.startedAtMs, monoMs: null });
  }
}

/** Test seam: forget every feed, as a page load does. */
export function resetPolledFillSound(): void {
  feeds.clear();
}
